<template>
  <div class="labels-container" :class="{ 'single-line': singleLine, 'size-small': size === 'small' }">
    <label v-if="!hideLabel">{{ resolvedLabel }}</label>

    <div class="id-copy-field">
      <v-tooltip location="top" content-class="id-tooltip-small">
        <template #activator="{ props: tooltipProps }">
          <span
            v-bind="tooltipProps"
            :id="`${type}-id-${parseId(id)}-val`"
            :class="{ hyperlink: canNavigate }"
            @click.stop="navigate"
          >{{ parseId(id) }}</span>
        </template>
        <span style="font-size: 0.7rem; line-height: 1; display: block;">ID: {{ id }}</span>
      </v-tooltip>
      <SVGIcon
        v-tooltip="`Copy ${IDPrefix} ID to clipboard`"
        class="icon copy"
        :iconName="'copy'"
        :width="'16px'"
        :height="'16px'"
        @click.stop="copyToClipboard(parseId(id))"
      />
    </div>
  </div>
</template>

<script setup>
import {
  ref, toRefs, computed,
} from 'vue';
import SVGIcon from '@/components/SVGIcon.vue';
import { copyToClipboard as safeCopy } from '@/utils/clipboard';
import {
  goto_project, goto_experiment, goto_dataset_gallery, goto_trainer_session, goto_validation_session, goto_snapshots,
} from '@/assets/js/links.js';

const props = defineProps({
  id: {
    type: Number,
    default: 0,
  },
  type: {
    type: String,
    default: '',
  },
  hideLabel: {
    type: Boolean,
    default: false,
  },
  singleLine: {
    type: Boolean,
    default: false,
  },
  labelOverride: {
    type: String,
    default: '',
  },
  size: {
    type: String,
    default: 'default',
  },
  hyperlink: {
    type: Boolean,
    default: false,
  },
  projectId: {
    type: Number,
    default: null,
  },
  experimentId: {
    type: Number,
    default: null,
  },
});

const IDPrefix = ref(null);

const {
  id,
  type,
  singleLine,
} = toRefs(props);

const resolvedLabel = computed(() => {
  const override = String(props.labelOverride || '').trim();
  if (override) {
    return override;
  }
  return type.value === 'task' ? 'Task:' : `${IDPrefix.value} ID`;
});

function parseId() {
  if (id.value && type.value) {
    let prefix = '';
    switch (type.value) {
    case 'task':
      prefix = 'bt';
      IDPrefix.value = "Task";
      break;
    case 'dataset':
      prefix = 'ds';
      IDPrefix.value = "Dataset";
      break;
    case 'annotation set':
      prefix = 'as';
      IDPrefix.value = "Annotation Set";
      break;
    case 'annotation':
      prefix = 'a';
      IDPrefix.value = "Annotation";
      break;
    case 'image':
      prefix = 'i';
      IDPrefix.value = "Image";
      break;
    case 'trainer':
      prefix = 't';
      IDPrefix.value = "Session";
      break;
    case 'validator':
      prefix = 'v';
      IDPrefix.value = "Session";
      break;
    case 'validation':
      prefix = 'v';
      IDPrefix.value = "Session";
      break;
    case 'project':
      prefix = 'p';
      IDPrefix.value = "Project";
      break;
    case 'experiment':
      prefix = 'exp';
      IDPrefix.value = "Experiment";
      break;
    case 'auto annotation':
      prefix = 'inf';
      IDPrefix.value = "Session";
      break;
    case 'sample':
      prefix = 's';
      IDPrefix.value = "Sample";
      break;
    case 'snapshot':
      prefix = 'ss';
      IDPrefix.value = "Snapshot";
      break;
    default:
      prefix = '';
    }
    let result = '';
    if (prefix !== '') {
      result = `${prefix}-${Number(id.value).toString(16)}`;
    } else {
      result = Number(id.value).toString(16);
    }

    return result;
  }
  return '';
}

function copyToClipboard(text) {
  safeCopy(text);
}

const canNavigate = computed(() => Boolean(props.hyperlink && props.id && props.type));

function navigate() {
  if (!canNavigate.value) return;

  switch (type.value) {
  case 'project':
    goto_project(id.value);
    break;
  case 'experiment':
    goto_experiment(props.projectId, id.value);
    break;
  case 'dataset':
    goto_dataset_gallery(id.value, props.projectId);
    break;
  case 'trainer':
    goto_trainer_session(props.projectId, props.experimentId, id.value);
    break;
  case 'validator':
  case 'validation':
    goto_validation_session(props.projectId, props.experimentId, id.value);
    break;
  case 'snapshot':
    goto_snapshots();
    break;
  default:
    break;
  }
}

</script>

<style lang="scss" scoped>
.labels-container {
  display: flex;
  flex-direction: column;
  label {
    font-size: 0.72rem;
    font-weight: 550;

    &.hide {
      visibility: hidden;
    }
  }

  .id-copy-field {
    display: flex;
    flex-direction: row;
    align-items: center;
    color: var(--body-text-color);
    gap: 8px;

    span {
      min-width: 44px;
    }

    span.hyperlink {
      cursor: pointer;
      font-weight: inherit;
      @include themify() {
        color: themed('color-primary');
      }
    }
  }

  .icon {
    margin: 0 5px 0 0;
    cursor: pointer;
    &:focus,
    &:focus-visible {
      outline: none;
    }
    &.copy{
      color: #999;
      &:hover {
        color: #666;
      }
    }
  }

  :deep(.id-tooltip-small) {
    padding: 1px 3px !important;
    line-height: 1 !important;
    min-height: auto !important;
    background-color: #fff !important;
    color: #0066cc !important;
  }

  &.single-line {
    flex-direction: row;
    align-items: center;
    gap: 6px;
    width: 100%;
    margin-top: 0;
    margin-bottom: 0;

    label {
      white-space: nowrap;
      flex: 0 0 auto;
    }

    .id-copy-field {
      flex: 1;
    }
  }

  &.size-small {
    label {
      font-size: 0.7rem;
    }

    .id-copy-field {
      span {
        font-size: 0.75rem;
      }
    }
  }
}
</style>
